/**
 * AC4's PS half, expressed as the condition AC4 actually states.
 *
 * AC4: "On a project group (program) timeline and on a PS project timeline, where
 * there is no single project to list sets for, the button is not shown."
 *
 * The clause that matters is the last one. PIKA-748 implemented the PS half as
 * `!viewer.psProject`, on the belief that a resolved psProject marks a project
 * service project. It does not: `ViewerType.js:797` resolves psProject by asking
 * project-service for a row keyed on the project, and ordinary projects have one.
 * The clause was therefore false everywhere, and shipped the button, the chip and
 * the drawer all disabled (PIKA-773, PIKA-774).
 *
 * `legacyProjectAsSingleProject` answers the real question. A project service
 * project that maps to exactly one legacy project HAS a single project whose sets
 * can be listed, and the surface is legitimate there. One that fans out to many, or
 * to none, resolves null — and that is the case AC4 excludes.
 *
 * Deliberately NOT keyed on psProject being present, and deliberately permissive
 * when there is no psProject at all: a program timeline resolves none, and the
 * 22 September decision keeps the chip drawn there.
 */

export interface TaskSetSurfaceViewer {
	psProject?: {legacyProjectAsSingleProject?: {id?: string} | null} | null;
}

/**
 * False only where a project service project resolves no single legacy project.
 *
 * The narrow reading is on purpose. A gate that is wrong in the permissive
 * direction shows a button somewhere it need not be; wrong in the restrictive
 * direction it removes the entire feature, which is what happened twice.
 */
export const hasSingleProjectForTaskSets = (viewer?: TaskSetSurfaceViewer | null): boolean => {
	const psProject = viewer?.psProject;

	if (!psProject) {
		return true;
	}

	return !!psProject.legacyProjectAsSingleProject;
};
